// @ts-ignore esModuleInterop
import React, { useEffect, useRef, useState } from 'react'
import Urn from '@adc/structures/urn'
import { useDispatch } from 'react-redux'
import type { AnyAction } from 'redux'
import type { ThunkDispatch } from 'redux-thunk'
import moment from 'moment-timezone'
// @ts-ignore no types
import _ from 'lodash'
import _cx from 'classnames/bind'
// @ts-ignore no types
import numeral from 'numeral'
import * as graph from '@adc/sdk'
import { match } from 'ts-pattern'
import {
  Alert,
  Divider,
  Icon,
  Text,
  constants,
  utils,
  ListingReserve,
  ScrollSpy,
  useApplicationContext,
  useApplicationModals,
  PhoneVerification,
  NotifyMeModal,
  ModalIds,
  // @ts-ignore no types
} from '@adc/ui-adc'
import type { AuctionBlueprintOnlineReactiveArgs } from '@adc/blueprint'
import type { Listing } from '@adc/sdk/types'
import * as blueprint from '@adc/blueprint'
import newAnalytics from '@adc/analytics'
// @ts-ignore no types
import { contactNumbers } from '@adc/constants'
import CreditBidLoginModal from '../../components/creditBidLoginModal'
import { updatePhoneValue, PHONE_FIELDS } from '../../actions/user'
import { usePropertyContext } from '../../context/property'
import { useListingContext } from '../../context/listing'
import analytics from '../../services/analytics'
import PopupTooltip from '../../components/popupTooltip'
import { getMarketingAttribute } from '../../utils/graph'
import { formatMoneyWithCentsExceptZero } from '../../utils/formatters'
import ERVLowerModal from '../../components/ervLowerModal'
import {
  NOTIFY_DEFAULT_TEXT,
  NOTIFY_SUBMITTED_TEXT,
  NOTIFY_RESERVE,
  EST_DEBT_TOOLTIP_CONTENT,
  RENTAL_ESTIMATE_DESCRIPTION,
  ARV_TOOLTIP_CONTENT,
  ERV_LOWER_RESERVE,
  ERV_LOWER_CREDIT_BID,
} from '../../statics'
import { getListPrice } from '../../utils/listing'
// @ts-ignore no types
import { isAuctionSold } from '../auctionDetails/utils'
import CreditBidAlert from './CreditBidAlert'
import cls from './index.scss'

const cx = _cx.bind(cls)

/**
 * NOTE: We have other reserve tooltips elsewhere, notably in the bid banner. They are slightly different to the
 * this which is why they aren't used here. From inspecting history that looks intentional to makie that message more
 * contextual for when the auction is ongoing.
 */
const RESERVE_TOOLTIP_CONTENT = (
  <div>
    <p>This is the lowest acceptable price by the seller.</p>
    <p>
      Sellers don't always disclose the Reserve, but if they do, we can notify
      you
    </p>
  </div>
)

const EstCreditBidTooltipContent = ({
  amountShown,
  isCreditBidRevealEnabled,
}: {
  amountShown: boolean
  isCreditBidRevealEnabled: boolean
}) => {
  if (isCreditBidRevealEnabled && amountShown) {
    return (
      <div>
        <p>
          The amount bids must exceed to purchase the property. The highest bid
          above it wins in the end.
        </p>
      </div>
    )
  }

  if (!amountShown) {
    return (
      <div>
        <p>This is the estimated amount the lender will bid at the sale.</p>
        <p>
          Lenders don't always disclose a Credit Bid, but if they do, it's
          usually shared just before the sale.
        </p>
      </div>
    )
  }

  return (
    <div>
      <p>This is the estimated amount the lender will bid at the sale.</p>
      <p>
        Buyers will have to bid above this amount to purchase at auction. Amount
        subject to change.
      </p>
    </div>
  )
}

/**
 * This value seems to have been copy/pasted in lots of places in this app. I have extracted here so we can do exact
 * matches without copy/pasting strings throughout the file.
 *
 * TODO: Extract this into constant and replace hard coded instances of this string that are present for the same
 * purpose.
 */
const NULL_VALUE = 'TBD'

numeral.defaultFormat('$0,0')
numeral.nullFormat(NULL_VALUE)

const { marketingAttributeTypes } = constants.marketingAttributeTypes
const defaultAttributeMap = marketingAttributeTypes.reduce(
  (prev: any, { text, value }: { text: any; value: any }) => {
    prev[value] = text
    return prev
  },
  {}
)
interface ValuationRowProps {
  elmId: string
  firstCol: any
  secondCol: any
  annotation?: any
}
export const ValuationRow = ({
  elmId,
  firstCol,
  secondCol,
  annotation,
}: ValuationRowProps) => (
  <div className={cx('valuation-row')} data-elm-id={`${elmId}_row`}>
    <div className={cx('row')}>
      <div className={cx('label')}>
        <Text
          as='span'
          color={constants.ColorNames.Gray}
          data-elm-id={`${elmId}_label`}
        >
          {firstCol}
        </Text>
      </div>
      <div className={cx('value')}>
        <Text
          color={constants.ColorNames.Gray}
          weight='heavy'
          data-elm-id={`${elmId}_value`}
        >
          {secondCol}
        </Text>
      </div>
      {annotation && <div className={cx('annotation')}>{annotation}</div>}
    </div>
  </div>
)

interface MarketingAttributeProps {
  listing: AuctionBlueprintOnlineReactiveArgs['listing']
  attributeType: string
  value: any
  tooltip?: any
  format: any
  elmId: string
}

const MarketingAttribute = ({
  listing,
  attributeType,
  value,
  tooltip,
  format = _.identity,
  elmId = 'marketing_attribute',
}: MarketingAttributeProps) => {
  const attr = getMarketingAttribute(listing, attributeType)

  if (!attr.show || !(value || attr.default_display_value !== 'HIDDEN'))
    return null
  const defaultLabel = defaultAttributeMap[attr.attribute_type]
  const label = (
    <span className='u-pr-2'>
      {attr.label || defaultLabel}
      {tooltip && (
        <PopupTooltip
          content={tooltip}
          elmId={`${elmId}_popup_tooltip`}
          position='top center'
          trigger={<Icon name='info circle' data-elm-id={`${elmId}_popup`} />}
        />
      )}
    </span>
  )
  return (
    <ValuationRow
      elmId={elmId}
      firstCol={label}
      secondCol={value ? format(value) : attr.default_display_value}
    />
  )
}

const isReserveDisplayed = (
  listing: AuctionBlueprintOnlineReactiveArgs['listing']
) => _.get(listing, 'listing_configuration.is_reserve_displayed', false)

interface ReserveInfoProps {
  elmId: string
  listing: AuctionBlueprintOnlineReactiveArgs['listing']
  notifyMeTrigger: React.ReactNode
  toggleSaveProperty?: any
}

const RequiresAuthenticatedUser = ({
  children,
  label,
  className,
}: {
  children: React.ReactNode
  label?: string
  className?: string
}): React.ReactElement | null => {
  const { user } = useApplicationContext()
  const { open } = useApplicationModals()

  const onClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    open('authentication', {})
  }

  // @ts-ignore I am not doing the untypedUser stuff. When it's typed it will be typed
  if (!user.logged_in) {
    return (
      <a href='/login' onClick={onClick} className={className}>
        {label || 'Log in to view'}
      </a>
    )
  }

  return children ? <>{children}</> : null
}

const ReserveInfo = ({
  elmId,
  listing,
  notifyMeTrigger,
  toggleSaveProperty,
}: ReserveInfoProps) => {
  const { featureFlags, user } = useApplicationContext()
  const { segment } = useListingContext()
  const { open: openAuthModal } = useApplicationModals(
    ModalIds.Authentication,
    {
      authInitialActive: 'login',
    }
  )
  const { property } = usePropertyContext()
  const isPropertySaved = Boolean(property.is_currently_saved)
  const isCreditBidRevealEnabled =
    featureFlags?.['fe.resi.enable-credit-bid-reveal']
  const isNotifyMe2Enabled = Boolean(
    featureFlags?.['fe.resi.enable-notify-me-2']
  )

  // Notify Me 2.0 modal open state
  const dispatch: ThunkDispatch<any, any, AnyAction> = useDispatch()
  const [notifyMeOpen, setNotifyMeOpen] = useState(false)
  // Intent recorded when a logged-out user clicks Notify Me; survives in-page
  // login so the effect below can reopen the modal.
  const pendingNotifyMeRef = useRef(false)
  const partyId = (user as any)?.party_id

  // Blueprint owns the marketing communication preferences (fetch + opt-in).
  const commPrefs =
    blueprint.communicationPreferences.useCommunicationPreferences({
      searchUrn: partyId ? new Urn('party', partyId).toString() : undefined,
      email: (user as any)?.email,
    })

  // Notify Me 2.0 owns the SMS/email enrollment writes. Even when the user is
  // Twilio-blocked we still show the full flow — the modal surfaces the orange
  // "text START" warning on the opt-in step and disables submit.
  const notifyMe2Active = isNotifyMe2Enabled

  // Reopen after login: logged_in flips true before party_id loads, and the
  // modal is gated on partyId, so wait for both.
  useEffect(() => {
    if (!pendingNotifyMeRef.current || !user?.logged_in || !partyId) return
    pendingNotifyMeRef.current = false
    setNotifyMeOpen(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.logged_in, partyId])

  const latestPhone =
    PhoneVerification.utils.getLatestPhone((user as any)?.party?.phones) || {}

  // Optimistic phone update; marketing enrollment happens in the modal.
  const handlePhoneVerified = ({ number }: { number: string }) => {
    dispatch(updatePhoneValue(number, PHONE_FIELDS.optIn, true))
    dispatch(updatePhoneValue(number, PHONE_FIELDS.verified, true))
  }

  const creditBidState = blueprint.listing.utils.getCreditBidState({
    featureFlags: featureFlags as Record<string, boolean>,
    listing,
    user: user || { logged_in: false },
    isPropertySaved,
    isAuctionActive: blueprint.listing.utils.isAuctionActive(
      listing,
      segment as Listing['selling_method']
    ),
  })

  useEffect(() => {
    if (creditBidState !== 'coming-soon') return
    newAnalytics.track(newAnalytics.eventNames.notifyMeCtaShown)
  }, [creditBidState])

  const handleNotifyMeClick = () => {
    newAnalytics.track(newAnalytics.eventNames.notifyMeCtaClicked)
    if (notifyMe2Active) {
      if (!user?.logged_in) {
        pendingNotifyMeRef.current = true
        openAuthModal()
        return
      }
      // Save is deferred to handleNotifyMeClose so it doesn't flip
      // isPropertySaved mid-session and unmount the modal.
      setNotifyMeOpen(true)
    } else {
      toggleSaveProperty?.({ source: NOTIFY_RESERVE })
    }
  }

  // Save on close; the modal handles marketing enrollment.
  const handleNotifyMeClose = () => {
    setNotifyMeOpen(false)
    toggleSaveProperty?.({ source: NOTIFY_RESERVE })
  }

  // eslint disables because ui-adc not typed yet
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
  const [reserveLabel, value] = ListingReserve.rawTuple?.(
    listing,
    NULL_VALUE
  ) ?? [NULL_VALUE, NULL_VALUE]

  if (!isReserveDisplayed(listing)) return null

  const undisclosedReserve = value === NULL_VALUE

  const tooltip = listing.event?.trustee_sale ? (
    <EstCreditBidTooltipContent
      amountShown={!undisclosedReserve}
      isCreditBidRevealEnabled={Boolean(isCreditBidRevealEnabled)}
    />
  ) : (
    RESERVE_TOOLTIP_CONTENT
  )

  // NOTE: Slightly confusing down below but even though this is the same value, for REO we won't show the tooltip if
  // there is no value (I don't know why since it would still be explaining what the row is for) but trustee will
  // always show a tooltip but change language inside depending on whether the reserve is on the page or not.
  const label = (
    <span className='u-pr-2'>
      {reserveLabel}{' '}
      {(listing.event?.trustee_sale || undisclosedReserve) && (
        <PopupTooltip
          content={
            <section data-elm-id='reserve_not_disclosed_popup_tooltip'>
              {tooltip}
            </section>
          }
          elmId={`${elmId}_popup_tooltip`}
          position='top center'
          trigger={<Icon name='info circle' data-elm-id={`${elmId}_popup`} />}
        />
      )}
    </span>
  )

  // No value: show Notify Me for everyone. Value present: gate behind auth.
  const rowValue = undisclosedReserve ? (
    notifyMeTrigger
  ) : (
    <RequiresAuthenticatedUser children={value} />
  )

  if (creditBidState === 'legacy') {
    return (
      <ValuationRow elmId='reserve' firstCol={label} secondCol={rowValue} />
    )
  }

  return match(creditBidState)
    .with('hidden', () => null)
    .with('disclosed', () => (
      <Alert
        role='success'
        type='inline'
        elmId='credit_bid_highlight'
        includeShadow
        compact
        style={{ marginRight: 7, marginLeft: 7 }}
        header={
          <div className={cx('credit-bid-highlight')}>
            <div
              className={cx('credit-bid-row')}
              data-elm-id='credit_bid_highlight_row'
            >
              <Text
                as='span'
                color=''
                weight='medium'
                className={cx('credit-bid-label-dark')}
              >
                <Icon
                  name='badge-dollar'
                  iconStyle='solid'
                  color='unspecified'
                  className={cx('credit-bid-icon')}
                />
                {reserveLabel}{' '}
                <PopupTooltip
                  content={
                    <section data-elm-id='reserve_not_disclosed_popup_tooltip'>
                      {tooltip}
                    </section>
                  }
                  elmId={`${elmId}_popup_tooltip`}
                  position='top center'
                  trigger={
                    <Icon name='info circle' data-elm-id={`${elmId}_popup`} />
                  }
                />
              </Text>
              <Text
                as='span'
                color=''
                className={cx('credit-bid-value')}
                data-elm-id='credit_bid_highlight_value'
              >
                {value}
              </Text>
            </div>
            <Text
              // Non-standard 13px per design spec (no DS token exists for this size)
              style={{ fontSize: 13 }}
              color=''
              className={cx('credit-bid-helper')}
              data-elm-id='credit_bid_highlight_helper'
            >
              Know the price to beat, and bid with confidence.
            </Text>
          </div>
        }
      />
    ))
    .with('available', () => (
      <CreditBidAlert
        role='success'
        elmId='credit_bid_available'
        iconName='badge-dollar'
        title='Est. Credit Bid Is Available!'
        description='Know the bid to beat before the auction starts.'
        descriptionElmId='credit_bid_available_helper'
        action={
          <div
            className={cx('credit-bid-notify', 'credit-bid-notify-success')}
            data-elm-id='credit_bid_available_button'
          >
            <RequiresAuthenticatedUser
              label='Log In to View'
              className={cx('credit-bid-notify-label')}
            >
              {null}
            </RequiresAuthenticatedUser>
          </div>
        }
      />
    ))
    .with('subscribed', () => (
      <CreditBidAlert
        role=''
        elmId='credit_bid_subscribed'
        iconName='bell-on'
        title='Est. Credit Bid Coming Soon'
        description="You'll be notified when the Credit Bid is available"
        descriptionElmId='credit_bid_subscribed_helper'
      />
    ))
    .otherwise(() => (
      <>
        <CreditBidAlert
          role='info'
          elmId='credit_bid_not_available'
          title='Est. Credit Bid Is Coming Soon'
          description='Credit Bid is the bid to beat. Highest bid above it wins.'
          descriptionElmId='credit_bid_coming_soon_helper'
          action={
            <div
              className={cx('credit-bid-notify', 'credit-bid-notify-info')}
              data-elm-id='credit_bid_coming_soon_button'
              onClick={handleNotifyMeClick}
            >
              <Icon
                name='bell'
                iconStyle='solid'
                color='unspecified'
                className={cx('credit-bid-notify-icon')}
              />
              <span
                className={cx('credit-bid-notify-label')}
                data-elm-id='get_credit_bid_alerts'
              >
                Notify Me When Available
              </span>
            </div>
          }
        />
        {partyId && (
          <NotifyMeModal
            open={notifyMeOpen}
            onClose={handleNotifyMeClose}
            onConfirm={() => setNotifyMeOpen(false)}
            partyId={partyId}
            latestPhone={latestPhone}
            onVerificationSuccess={handlePhoneVerified}
            communicationPreferences={commPrefs}
            smsOptOutNumber={String(
              utils.formatters.formatPhoneNumber(
                contactNumbers.OUTBOUND_SMS_NUMBER
              )
            )}
          />
        )}
      </>
    ))
}

interface ValuationDetailsProps {
  toggleSaveProperty?: any
  realtyTrackReport?: any
  showRentalEstimate?: boolean
  isPropertyReportAvailable?: boolean
}
const AfterRepairValue = ({
  isPropertyReportAvailable,
}: {
  isPropertyReportAvailable: boolean
}) => {
  const { listing } = useListingContext()
  const { featureFlags } = useApplicationContext()

  // Dual-Track: on the private-seller path, suppress Est. Market Value when a
  // list price is published (keep it when there's no list price).
  const suppressEmv =
    featureFlags?.['fe.resi.enable-unified-pdp'] &&
    blueprint.listing.utils.isPrivateSeller(listing) &&
    blueprint.listing.utils.shouldDisplayListPrice(listing)

  const arv = listing?.primary_property?.summary?.valuation
  const arvLabel =
    typeof arv !== 'number' || isNaN(arv)
      ? 'Not Available'
      : numeral(arv).format()

  const shouldShowReserveOrCreditBidAmount = Boolean(
    listing.listing_configuration?.is_reserve_displayed
  )

  const shouldShowAnnotation =
    !isAuctionSold(listing) && // was bStatus.isAuctionSold
    shouldShowReserveOrCreditBidAmount &&
    arv &&
    listing.reserve_price &&
    arv < listing.reserve_price // was bStatus.estCreditBid || bStatus.bidValuationInformation.reserve_amount

  if (suppressEmv) return null

  return (
    <ValuationRow
      elmId='arv'
      firstCol={
        <span>
          Est. Market{' '}
          <span className='u-nowrap'>
            Value{' '}
            <PopupTooltip
              content={ARV_TOOLTIP_CONTENT}
              elmId='arv_popup_tooltip'
              position='top center'
              trigger={<Icon name='info circle' data-elm-id='arv_popup' />}
            />
          </span>
        </span>
      }
      secondCol={arvLabel}
      annotation={
        <div>
          {isPropertyReportAvailable && arv && !isNaN(arv) && (
            <div className={cx('u-display-f', 'u-display-f-between')}>
              <a href='#comp_sales'>See Comparable Sales</a>
              <Text
                className={cx('ca-source', 'u-mb-1')}
                color={constants.ColorNames.Gray}
                size='mini'
              >
                Source: Cotality
              </Text>
            </div>
          )}
          {shouldShowAnnotation && (
            <ERVLowerModal
              type={listing.auction?.is_online ? 'reo' : 'tps'}
              trigger={
                <Text size='tiny' className='u-pb-1'>
                  <a data-elm-id='erv_lower_reserve_modal_trigger'>
                    {listing.auction?.is_online
                      ? ERV_LOWER_RESERVE
                      : ERV_LOWER_CREDIT_BID}
                  </a>
                </Text>
              }
            />
          )}
        </div>
      }
    />
  )
}

const NotifyMeButton = ({
  toggleSaveProperty,
}: {
  toggleSaveProperty: any
}) => {
  const { property } = usePropertyContext()

  if (property.is_currently_saved) {
    return (
      <Text
        color={constants.ColorNames.Gray}
        size='tiny'
        className={cx('notify-submitted-text')}
      >
        {NOTIFY_SUBMITTED_TEXT}
      </Text>
    )
  }

  const handleNotifyMe = () => {
    toggleSaveProperty({
      source: NOTIFY_RESERVE,
    })
  }

  return (
    <a onClick={handleNotifyMe} data-elm-id='notify_me_link'>
      {NOTIFY_DEFAULT_TEXT}
    </a>
  )
}
const openingBidFormatter = (val: number) => {
  const { listing } = useListingContext()
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return listing.listing_configuration?.asset_type ===
    graph.enums.AssetType.Foreclosure
    ? formatMoneyWithCentsExceptZero(val, 'TBD')
    : numeral(val).format('$0,0', Math.floor)
}

const AuctionEndDate = () => {
  const { segment, listing } = useListingContext()
  return segment?.segment_status ===
    graph.enums.OnlineAuctionSegmentStatus.Ended &&
    segment?.subject_to_status === graph.enums.SubjectToStatus.Pending &&
    listing.listing_status !== graph.enums.ListingStatus.Closed ? ( // bStatus.isSoldPending
    <div data-elm-id='auction_end_date_root'>
      <ValuationRow
        elmId='auction_end_date'
        firstCol='Auction Ended'
        secondCol={moment(segment.initial_end_date).format('MM/DD/YYYY')} // bStatus.bidValuationInformation.formatted_end_date, was formatted like format('MM/DD/YYYY')
      />
    </div>
  ) : null
}

const renderStateDeposit = (
  segment: blueprint.AuctionBlueprintLiveReactiveArgs['segment'],
  listing: blueprint.AuctionBlueprintLiveReactiveArgs['listing']
) => {
  if (!segment?.__methods__?.getStateDepositValues) return null
  const { stateDepositRule, stateDepositLabel, shouldShowStateDeposit } =
    segment.__methods__.getStateDepositValues()

  if (!shouldShowStateDeposit) return null

  const propertyStateLowercaseAbbreviation = _.get(
    listing,
    'primary_property.summary.address.country_primary_subdivision',
    ''
  ).toLowerCase()

  const trackPopupOpened = () => {
    analytics.popup(
      `${propertyStateLowercaseAbbreviation}_winning_bid_deposit`,
      {
        event: 'hover-event',
      }
    )
  }

  const firstColumn = (
    <span className='u-pr-2'>
      Winning Bid Deposit{' '}
      <PopupTooltip
        content={
          <ScrollSpy selector='#prepare_your_finances'>
            {({ scrollTo }: { scrollTo: any }) => (
              <>
                A Winning Bid Deposit is required for each property you
                successfully bid on and must be paid upon being declared the
                winning bidder. Further action will be required to submit the
                remaining balance.{' '}
                <a
                  onClick={scrollTo}
                  data-elm-id='prepare_for_auction_tooltip-content'
                  className={cx('lighter-link-tooltip')}
                >
                  Learn more
                </a>
              </>
            )}
          </ScrollSpy>
        }
        elmId='state_deposit_popup_tooltip'
        position='top center'
        trigger={<Icon name='info circle' data-elm-id='state_deposit_popup' />}
        onOpen={trackPopupOpened}
      />
    </span>
  )
  return (
    stateDepositRule && (
      <div data-elm-id='state_deposit_root' className={cx('state-deposit-row')}>
        <ValuationRow
          elmId='state_deposit'
          firstCol={firstColumn}
          secondCol={stateDepositLabel}
        />
      </div>
    )
  )
}
const numeralFormat = (val: number) => numeral(val).format()

const renderHotIcon = (
  listing: AuctionBlueprintOnlineReactiveArgs['listing']
) => {
  return (
    listing && listing?.is_hot && <span className={cx('hot-icon')}>HOT</span>
  )
}
const renderPotentialReturnMessage = (
  listing: AuctionBlueprintOnlineReactiveArgs['listing']
) => {
  return (
    listing &&
    listing.is_hot && (
      <Text color={constants.ColorNames.Gray} size='tiny' className='u-mt-3'>
        This investment property may have a higher potential return compared to
        similar properties.{' '}
        <a target='_blank' rel='noreferrer' href='/lp/learn-more/glossary/#h'>
          Find out why in the Help Center.
        </a>
      </Text>
    )
  )
}
const MarketingAttributes = () => {
  const { listing, segment } = useListingContext()
  const { featureFlags } = useApplicationContext()
  const isCreditBidRevealEnabled =
    featureFlags?.['fe.resi.enable-credit-bid-reveal']
  const isCanceled =
    isCreditBidRevealEnabled &&
    listing.listing_status === graph.enums.ListingStatus.Canceled

  const showListPrice = listing.__methods__.shouldDisplayListPrice()
  const showOpeningBidRow =
    !showListPrice && !isCanceled && listing.listing_summary?.show_opening_bid // listing.listing_summary?.show_opening_bid replaced bStatus.showOpeningBid
  const estDebt = parseInt(
    // @ts-ignore TODO:
    _.get(listing, 'auction.bid_instruction.nos_amount', 0),
    10
  )

  const showEstDebtRow = Number.isFinite(estDebt)

  return (
    <>
      {showListPrice && (
        <ValuationRow
          elmId='list_price'
          firstCol='List Price'
          secondCol={getListPrice(listing)}
        />
      )}
      {showOpeningBidRow && (
        <MarketingAttribute
          elmId='opening_bid'
          listing={listing}
          attributeType='OPENING_BID'
          value={
            segment?.starting_bid_amount ||
            _.get(listing, 'auction.starting_bid', 0)
          }
          format={openingBidFormatter}
        />
      )}
      {showEstDebtRow && (
        <MarketingAttribute
          elmId='estimated_debt'
          listing={listing}
          attributeType='NOS_AMOUNT'
          tooltip={EST_DEBT_TOOLTIP_CONTENT}
          value={estDebt}
          format={numeralFormat}
        />
      )}
      <MarketingAttribute
        elmId='original_balance'
        listing={listing}
        attributeType='ORIGINAL_PRINCIPAL_BALANCE'
        tooltip={EST_DEBT_TOOLTIP_CONTENT}
        value={_.get(
          listing,
          'listing_configuration.principal_original_amount',
          0
        )}
        format={numeralFormat}
      />
    </>
  )
}

const renderRentalEstimate = (realtyTrackReport: any) => {
  const value = _.get(realtyTrackReport, 'rentalRange.estimated')
  const elmId = 'rental_estimate_row'
  const firstCol = (
    <div className={cx('rental-estimate')}>
      <span>
        Rental Estimate{' '}
        <PopupTooltip
          content={RENTAL_ESTIMATE_DESCRIPTION}
          position='top center'
          elmId={`${elmId}_popup_tooltip`}
          trigger={
            <Icon name='info circle' data-elm-id={`${elmId}_popup_trigger`} />
          }
        />
      </span>
    </div>
  )

  const annotation = (
    <div className={cx('u-display-f', 'u-display-f-between')}>
      <a href='#comp_sales'>See Details</a>
      <Text
        className={cx('ca-source', 'u-mb-1')}
        color={constants.ColorNames.Gray}
        size='mini'
      >
        Source: Cotality
      </Text>
    </div>
  )

  return (
    <ValuationRow
      elmId={elmId}
      firstCol={firstCol}
      secondCol={numeral(value).format()}
      annotation={Boolean(+value) && annotation}
    />
  )
}
const ValuationDetails = (props: ValuationDetailsProps) => {
  const {
    toggleSaveProperty,
    realtyTrackReport,
    showRentalEstimate,
    isPropertyReportAvailable,
  } = props

  const { segment, listing } = useListingContext()
  const { property } = usePropertyContext()
  const { featureFlags, user } = useApplicationContext()
  const isCreditBidRevealEnabled =
    featureFlags?.['fe.resi.enable-credit-bid-reveal']
  const isNotifyMe2Enabled = Boolean(
    featureFlags?.['fe.resi.enable-notify-me-2']
  )
  const isCanceled =
    isCreditBidRevealEnabled &&
    listing.listing_status === graph.enums.ListingStatus.Canceled
  const showReserveRow =
    !isAuctionSold(listing) &&
    !isCanceled &&
    listing.listing_configuration?.is_reserve_displayed
  // NOTE: we're making an exception for this casting due to these components being specifically for live auctions
  const liveSegment =
    segment as unknown as blueprint.AuctionBlueprintLiveReactiveArgs['segment']

  // Future auction end: online uses initial_end_date, live uses auction_date.
  // Pass the reactive segment so live-bidding end dates take precedence.
  const isAuctionActive = blueprint.listing.utils.isAuctionActive(
    listing,
    segment as Listing['selling_method']
  )

  const isCreditBidAvailable =
    blueprint.listing.utils.getCreditBidState({
      featureFlags: featureFlags as Record<string, boolean>,
      listing,
      user: user || { logged_in: false },
      isPropertySaved: Boolean(property?.is_currently_saved),
      isAuctionActive,
    }) === 'available'

  return (
    <div className={cx('investment-info')}>
      {isNotifyMe2Enabled && (
        <CreditBidLoginModal
          isLoggedOut={!user?.logged_in}
          isCreditBidRevealed={isCreditBidAvailable}
          isAuctionActive={isAuctionActive}
          isTrusteeSale={Boolean(listing.event?.trustee_sale)}
        />
      )}
      <Text.Header
        as='h2'
        level={1}
        compact
        // @ts-ignore fix types in DS, export enum if possible
        size='huge'
        weight='medium'
        color={constants.ColorNames.GrayDark}
      >
        {renderHotIcon(listing)} Price Insights
      </Text.Header>
      {renderPotentialReturnMessage(listing)}
      <div>
        {!listing.listing_configuration?.is_direct_offer_enabled &&
          showReserveRow && (
            <div className={cx('credit-bid-wrapper')}>
              <ReserveInfo
                elmId='reserve'
                listing={listing}
                toggleSaveProperty={toggleSaveProperty}
                notifyMeTrigger={
                  <NotifyMeButton toggleSaveProperty={toggleSaveProperty} />
                }
              />
              {Boolean(isCreditBidRevealEnabled) &&
                Boolean(listing.event?.trustee_sale) && (
                  <Divider style={{ marginTop: 18, marginBottom: 0 }} />
                )}
            </div>
          )}
        {(!isCreditBidRevealEnabled ||
          listing.listing_status !== graph.enums.ListingStatus.Canceled) && (
          <AfterRepairValue
            isPropertyReportAvailable={!!isPropertyReportAvailable}
          />
        )}
        {!listing.listing_configuration?.is_direct_offer_enabled && (
          <MarketingAttributes />
        )}
        {showRentalEstimate && renderRentalEstimate(realtyTrackReport)}
        {!listing.listing_configuration?.is_direct_offer_enabled && (
          <AuctionEndDate />
        )}
        {!listing.auction?.is_online &&
          segment?.__methods__ &&
          renderStateDeposit(liveSegment, listing)}
      </div>
    </div>
  )
}

export default ValuationDetails
